<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'RentalServiceCostAllocator',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
    },
    service: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const item = ref(null);
  const labelAlign = ref('right');
  const editor = ref(null);
  const formName = ref('rentalServiceCostAllocator');
  const formId = ref(null);
  const createTitle = ref(
    $fn.value.pgettext('RentalServiceCostAllocator', 'New cost allocator')
  );
  const resourceTitle = ref(
    $fn.value.pgettext('RentalServiceCostAllocator/header', 'Cost allocator')
  );
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));
  const b_7789867021953041_dataSource = computed(() => ({
    name: 'suggest_meter',
    parameters: {
      types_only: 'cost_allocator',
    },
  }));

  const $data = computed(() => ({
    item: item.value,
    labelAlign: labelAlign.value,
    editor: editor.value,
    formName: formName.value,
    formId: formId.value,
    createTitle: createTitle.value,
    resourceTitle: resourceTitle.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_7789867021953041_dataSource: b_7789867021953041_dataSource.value,
  }));

  const $$load_item = () => {
    return props.oid
      ? $fn.value.fetch('read_rental_service_cost_allocator', {
          parameters: { id: props.oid },
          asResource: true,
        })
      : $fn.value.schemaDefaults('CreateRentalServiceCostAllocator', {
          rental_id: props.service.rental.id,
          rental_service_id: props.service.id,
        });
  };
  const $$load_editor = () => {
    return item.value.editor();
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'labelAlign' },
      { name: 'editor', dependsOn: ['item'] },
      { name: 'formName' },
      { name: 'formId' },
      { name: 'createTitle' },
      { name: 'resourceTitle' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_7789867021953041_dataSource', nodeId: '7789867021953041' },
    ],
    loaders: {
      item: $$load_item,
      editor: $$load_editor,
      formId: $$load_formId,
    },
    ux: {},
    dataRefs: {
      item,
      labelAlign,
      editor,
      formName,
      formId,
      createTitle,
      resourceTitle,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_7789867021953041_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          !props.oid && createTitle.value,
          item.value && item.value.meter && item.value.meter.serial_number,
        ],
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.service,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('service');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_wdy73dc6w_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeComponent_ih4ey072k_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_rest_rzp8ygg00_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: editor.value.cost_allocators,
      resource: editor.value,
    };
  };

  const act_7315092382398562_closeModal_wdy73dc6w =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_wdy73dc6w_getActionArgs,
    });
  const inst_evh_7315092382398562_close_mzam55kka =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_wdy73dc6w,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_mzam55kka],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_hptr2px58 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_4j6x7zggr =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_hptr2px58,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_4j6x7zggr],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_bar60anq4 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_83ykufp24 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_bar60anq4,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_83ykufp24],
    event: 'close',
  });
  const act_1040167445267876_closeComponent_ih4ey072k =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_ih4ey072k_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_t93g0eeam =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_ih4ey072k,
      event: 'saved',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_rest_rzp8ygg00 = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_rzp8ygg00_getActionArgs,
    displayName: 'create',
    events: [inst_evh_1040167445267876_saved_t93g0eeam],
  });
  const inst_evh_1040167445267876_submit_api3ci4ck =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_rzp8ygg00,
      event: 'submit',
      displayName: 'create',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_api3ci4ck],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_vpp57aq96 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_v55gbvms2 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_vpp57aq96,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_v55gbvms2],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_mzam55kka,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_4j6x7zggr,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_83ykufp24,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_saved_t93g0eeam,
    inst_evh_1040167445267876_submit_api3ci4ck,
    create: inst_evh_1040167445267876_submit_api3ci4ck,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_v55gbvms2,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_mzam55kka = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_mzam55kka.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_4j6x7zggr = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_4j6x7zggr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_83ykufp24 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_83ykufp24.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_t93g0eeam = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_t93g0eeam.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_api3ci4ck = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_api3ci4ck.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_v55gbvms2 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_v55gbvms2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="RentalServiceCostAllocator"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :dialogName="resourceTitle"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        :id="formId"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="meter_id"
          :label="pgettext('RentalServiceCostAllocator', 'Meter')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormSearchableSelect
            name="meter_id"
            v-model="editor.meter_id"
            :dataSource="b_7789867021953041_dataSource"
            :initialOptions="editor.meter ? [editor.meter] : []"
            valueField="id"
            textField="serial_number"
            use-option-template-for-selected-option
            optionTemplate='<UcSuggestMeterOption :option="option"></UcSuggestMeterOption>'
            required
            :readonly="item.isReadOnly"
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="factor"
          :label="pgettext('RentalServiceCostAllocator', 'Factor')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormInput
            type="number"
            name="factor"
            v-model="editor.factor"
            :readonly="item.isReadOnly"
            required
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="notes"
          :label="pgettext('RentalServiceCostAllocator', 'Notes')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
        >
          <ItpFormTextarea
            name="note"
            v-model="item.notes"
            :readonly="item.isReadOnly"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          type="submit"
          :form="forms[formName]"
          :spinning="actions.create.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.create.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
